import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { configureGoogleMapsLoader } from '../../lib/googleMapsRuntime';
import {
  SITE_COVERAGE_RADIUS_M,
  compassPoint,
  formatMeters,
  type LatLngPoint,
  type SiteViewpoint,
} from '../../lib/employerSiteViewpoints';
import type { SiteViewpointsStatus } from './useEmployerSiteViewpoints';

type Props = {
  building: LatLngPoint;
  employerName: string;
  viewpoint: SiteViewpoint | null;
  status: SiteViewpointsStatus;
  coverageRadiusM?: number;
  lookupError?: string | null;
  /** Slow orbit for guided employer address review. */
  slowRotate?: boolean;
};

export function EmployerSiteStreetStage({
  building,
  employerName,
  viewpoint,
  status,
  coverageRadiusM = SITE_COVERAGE_RADIUS_M,
  lookupError = null,
  slowRotate = false,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const panoRef = useRef<google.maps.StreetViewPanorama | null>(null);
  /** The host div unmounts whenever coverage drops out, so the pano must re-attach. */
  const attachedHostRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Every branch below that renders a StageMessage unmounts the panorama host, so
   * this must stay the exact union of those conditions — otherwise a live panorama
   * is left holding a detached host.
   */
  const hostVisible =
    status !== 'idle' &&
    status !== 'loading' &&
    status !== 'none' &&
    status !== 'error' &&
    !error &&
    Boolean(viewpoint);

  const disposePanorama = useCallback(() => {
    const pano = panoRef.current;
    if (pano) {
      try {
        pano.setVisible(false);
      } catch {
        // setVisible may throw if already disposed.
      }
    }
    panoRef.current = null;
    const host = attachedHostRef.current;
    attachedHostRef.current = null;
    host?.replaceChildren();
  }, []);

  useEffect(() => {
    if (!viewpoint) return;
    if (!configureGoogleMapsLoader()) {
      setError('Google Maps API key is not configured.');
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const { StreetViewPanorama } = await importLibrary('streetView');
        if (cancelled || !hostRef.current) return;

        if (!panoRef.current || attachedHostRef.current !== hostRef.current) {
          attachedHostRef.current = hostRef.current;
          panoRef.current = new StreetViewPanorama(hostRef.current, {
            pano: viewpoint.panoId,
            pov: { heading: viewpoint.heading, pitch: viewpoint.pitch },
            zoom: 0.6,
            addressControl: false,
            fullscreenControl: false,
            motionTracking: false,
            motionTrackingControl: false,
            linksControl: true,
            panControl: true,
            zoomControl: true,
          });
        }
        if (!cancelled) {
          setReady(true);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load Street View.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewpoint]);

  useEffect(() => {
    const pano = panoRef.current;
    if (!pano || !viewpoint) return;
    pano.setPano(viewpoint.panoId);
    pano.setPov({ heading: viewpoint.heading, pitch: viewpoint.pitch });
    pano.setZoom(0.6);
  }, [viewpoint]);

  useEffect(() => {
    if (!slowRotate || !viewpoint) return;
    let heading = viewpoint.heading;
    let frame = 0;
    const tick = () => {
      const pano = panoRef.current;
      if (pano) {
        heading = (heading + 0.07) % 360;
        pano.setPov({ heading, pitch: viewpoint.pitch });
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [slowRotate, viewpoint]);

  /**
   * A new building, a search, or a coverage gap swaps the host out for a message.
   * Releasing the panorama here keeps each inspect cycle from stranding one.
   */
  useEffect(() => {
    if (hostVisible) return;
    disposePanorama();
    setReady(false);
  }, [hostVisible, disposePanorama]);

  useEffect(() => disposePanorama, [disposePanorama]);

  const aimAtBuilding = useCallback(() => {
    const pano = panoRef.current;
    if (!pano || !viewpoint) return;
    pano.setPov({ heading: viewpoint.heading, pitch: viewpoint.pitch });
  }, [viewpoint]);

  if (!hostVisible) {
    if (status === 'loading' || status === 'idle') {
      return (
        <StageMessage>
          <CircularProgress size={28} />
        </StageMessage>
      );
    }

    if (status === 'none') {
      return (
        <StageMessage>
          <VisibilityOffRoundedIcon color="disabled" />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No Street View imagery within about {coverageRadiusM} m of this address (farther rings
            were also checked).
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Missing coverage is not evidence the employer is fake. Use satellite or 3D to check the
            building footprint instead.
          </Typography>
        </StageMessage>
      );
    }

    if (status === 'error' || error) {
      return (
        <StageMessage>
          <Typography variant="body2" color="error.main" textAlign="center">
            {lookupError ?? error ?? 'Street View lookup failed.'}
          </Typography>
        </StageMessage>
      );
    }

    return (
      <StageMessage>
        <Typography variant="body2" color="text.secondary">
          Pick a viewpoint to open Street View.
        </Typography>
      </StageMessage>
    );
  }

  // hostVisible already guarantees this; the compiler cannot see through it.
  if (!viewpoint) return null;

  const facing = compassPoint(viewpoint.heading);

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Box ref={hostRef} sx={{ height: '100%', width: '100%' }} />

      {!ready ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      <Stack
        spacing={0.25}
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          maxWidth: '70%',
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: 'rgba(17,24,39,0.8)',
          color: '#fff',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
          {viewpoint.title}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          Camera {formatMeters(viewpoint.distanceMeters)} away, looking {facing} at {employerName}
          {viewpoint.distantCoverage ? ' · may be a neighbouring block' : ''}
        </Typography>
        {viewpoint.imageDate ? (
          <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700 }}>
            Imagery date: {viewpoint.imageDate}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Imagery date: not provided by Street View
          </Typography>
        )}
        {viewpoint.description ? (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {viewpoint.description}
          </Typography>
        ) : null}
      </Stack>

      <Button
        size="small"
        variant="contained"
        startIcon={<ExploreRoundedIcon />}
        onClick={aimAtBuilding}
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 10,
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 999,
        }}
      >
        Re-aim at building
      </Button>

      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 12,
          right: 10,
          px: 1,
          py: 0.4,
          borderRadius: 1,
          bgcolor: 'rgba(17,24,39,0.78)',
          color: '#fff',
        }}
      >
        {building.lat.toFixed(5)}, {building.lng.toFixed(5)}
      </Typography>
    </Box>
  );
}

function StageMessage({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={1} alignItems="center">
        {children}
      </Stack>
    </Box>
  );
}
